import React, { useEffect, useMemo, useState } from "react";
import { useSelectedCustomer } from "@main/contexts/SelectedCustomerContext";
import { getApi } from "@beeldit/core/helpers/api";

const NextSessionsCards: React.FC = () => {
  const { selectedCustomer } = useSelectedCustomer();
  const [sessions, setSessions] = useState<any[]>([]);
  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const pageSize = 3;

  const formatDate = (iso?: string) => {
    if (!iso) return "";
    const d = new Date(iso);
    return d.toLocaleString("es-ES", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusInfo = (status: number) => {
    if (status === 1) return { label: "Realizada", color: "#22c55e" }; // verde
    if (status === 0) return { label: "Pendiente", color: "#f59e0b" }; // ámbar
    return { label: "", color: "#9ca3af" }; // gris por defecto
  };

  useEffect(() => {
    if (!selectedCustomer) return;
    setLoading(true);
    getApi()
      .get(`/user/customers/${selectedCustomer.id}/next-sessions`)
      .then((resp) => setSessions(resp.data))
      .catch((err) => setError("No se pudieron cargar las próximas sesiones"))
      .finally(() => setLoading(false));
  }, [selectedCustomer]);

  // Reinicia a la primera página cuando cambie el paciente o el resultado
  useEffect(() => {
    setPage(1);
  }, [selectedCustomer]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil((sessions?.length || 0) / pageSize)),
    [sessions, pageSize]
  );
  const paginatedSessions = useMemo(() => {
    const start = (page - 1) * pageSize;
    return sessions.slice(start, start + pageSize);
  }, [sessions, page, pageSize]);

  if (!selectedCustomer) return <div>Selecciona un paciente.</div>;
  if (error) return <div className="alert alert-danger">{error}</div>;
  if (loading)
    return (
      <div className="text-center my-4">
        <span className="spinner-border" /> Cargando...
      </div>
    );
  if (!sessions.length) return <div>No hay próximas sesiones.</div>;

  const prevDisabled = page <= 1;
  const nextDisabled = page >= totalPages;

  return (
    <div className="cards-view">
      <h4 className="my-3 text-center fw-bold">Próximas sesiones</h4>
      <div className="cards-list">
        {paginatedSessions.map((session, idx) => {
          const status = getStatusInfo(session.status);
          const treatmentName = session?.treatment?.name || "Sesión";
          return (
            <div className="card mx-3" key={session.id || `${page}-${idx}`}>
              <div className="card-header py-2 px-3">{treatmentName}</div>
              <div className="card-body pt-3">
                <p className="card-text mb-2">
                  {formatDate(session.start_date_time)} ({session.duration} min)
                </p>
                <div className="d-flex align-items-center">
                  <span
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      backgroundColor: status.color,
                      display: "inline-block",
                      marginRight: 8,
                    }}
                  />
                  <span>{status.label}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="cards-pagination d-flex justify-content-between align-items-center mt-2 px-3">
        <button
          className={`btn ${
            prevDisabled ? "btn-outline-secondary" : "btn-primary"
          }`}
          disabled={prevDisabled}
          onClick={() => setPage((p) => Math.max(1, p - 1))}
        >
          Anterior
        </button>
        <div>
          Página {page} de {totalPages}
        </div>
        <button
          className={`btn ${
            nextDisabled ? "btn-outline-secondary" : "btn-primary"
          }`}
          disabled={nextDisabled}
          onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
        >
          Siguiente
        </button>
      </div>
    </div>
  );
};

export default NextSessionsCards;
